import { useState, useEffect } from "react";
import { recordStudyTrialOffer } from "@/lib/study-conversion.functions";
import { Crown, CreditCard } from "lucide-react";
import { type PremiumState } from "@/lib/premium";
import { TRIAL_CANCEL_NOTE, TRIAL_HEADLINE } from "@/lib/trial";
import { useTrialEligibility } from "@/hooks/useTrialEligibility";
import { TrialConfirmDialog } from "@/components/TrialConfirmDialog";

/**
 * Always-visible trial offer for accounts that have not used their trial yet.
 * The trial is the monthly subscription's introductory period, so tapping it
 * opens the confirmation summary and then the payment sheet.
 */
export function TrialOfferCard({ state, surface = "home" }: { state: PremiumState | null; surface?: string }) {
  const [confirming, setConfirming] = useState(false);
  const { eligible } = useTrialEligibility(state);
  useEffect(() => { if (eligible && state?.authenticated) void recordStudyTrialOffer().catch(() => {}); }, [eligible, state?.authenticated]);

  // Apple/Google own introductory-offer eligibility — never advertise a trial
  // the store would refuse.
  if (!eligible) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        aria-label={`Start your ${TRIAL_HEADLINE} — payment method required`}
        className="group w-full rounded-2xl border border-neon-yellow/50 bg-gradient-to-br from-neon-yellow/15 via-neon-pink/10 to-neon-blue/10 p-4 text-left transition hover:border-neon-pink"
      >
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-neon-pink to-neon-yellow">
            <Crown className="size-5 text-white" />
          </span>
          <div className="flex-1">
            <p className="font-[family-name:var(--font-display)] text-sm font-extrabold text-ink">
              {TRIAL_HEADLINE}
            </p>
            <p className="text-xs text-ink-muted">
              Premium tutoring, AI Coach and quizzes. Payment method required to start.
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-neon-pink px-3 py-1.5 text-[11px] font-bold text-white transition group-hover:scale-105">
            Start trial
          </span>
        </div>
        <p className="mt-2 flex items-start gap-1 text-[11px] font-semibold text-ink">
          <CreditCard className="mt-0.5 size-3 shrink-0" /> Payment method required • Auto-renews until canceled •
          Cancel anytime
        </p>
        <p className="mt-1 text-[11px] font-semibold text-ink">{TRIAL_CANCEL_NOTE}</p>
      </button>
      {confirming && <TrialConfirmDialog surface={surface} onClose={() => setConfirming(false)} />}
    </>
  );
}
